import React, { useState, useCallback, useMemo } from "react"

import { useGetStoryblokItem } from "../../../../hooks/V2/storyblok/useItem"

import ProductInfoSection from "@components/V2/Sections/Info/ProductInfo"
import type { VariantsProps } from "@components/V2/Sections/Info/Variants"
import {
  useGetConsolidatedObjectFields,
  useGetPurchasableItem,
} from "@hooks/V2/storyblok"
import { getUrlFromStoryblokLink } from "@utils/storyblok"

const StoryblokProductInfoSection = ({
  blok,
  mobileGridOrder,
}: Storyblok.BlokProps<Storyblok.ProductInfoSection>) => {
  const getConsolidatedObjectFields = useGetConsolidatedObjectFields()
  const getPurchasableItem = useGetPurchasableItem()
  const [selectedOption, setSelectedOption] = useState(0)
  const getStoryblokItem = useGetStoryblokItem()

  const variantOptions: VariantsProps["options"] = useMemo(
    () =>
      blok.variants?.map(({ label = "", toastText, toastVariant }) => ({
        label,
        toast: toastText
          ? { text: toastText, variant: toastVariant }
          : undefined,
      })) || [],
    [blok.variants]
  )

  const handleVariantOptionChange = useCallback((id: number) => {
    setSelectedOption(id)
  }, [])

  const productConfigToUse = blok.variants[selectedOption].productConfig

  const storyblokItem = getStoryblokItem(productConfigToUse)

  const consolidatedFields = storyblokItem.content
    ? getConsolidatedObjectFields(storyblokItem.id, storyblokItem.content, 1)
    : undefined

  const purchasableItem = storyblokItem.content
    ? getPurchasableItem(storyblokItem.id, storyblokItem.content)
    : undefined

  if (!consolidatedFields || !purchasableItem) {
    return (
      <div className="col-span-full flex h-96 items-center justify-center">
        {productConfigToUse.content?.name} is not available at the moment
      </div>
    )
  }

  return (
    <ProductInfoSection
      mobileGridOrder={mobileGridOrder}
      galleryItems={consolidatedFields.galleryItems}
      superscript={blok.superscript}
      productName={consolidatedFields.name}
      showRecommendedProducts={
        consolidatedFields.showRecommendedProducts || false
      }
      recommendedProducts={consolidatedFields.recommendedProducts}
      recommendedLabel={consolidatedFields.recommendedLabel}
      // TODO: Convert long description to plain text and use that instead
      productDescription={consolidatedFields.description?.toString()}
      price={{
        adornmentText: "R",
        value: parseFloat(consolidatedFields.price),
        superscriptText: consolidatedFields?.originalPrice
          ? `was R${parseFloat(consolidatedFields.originalPrice)}`
          : undefined,
        sideScriptText: consolidatedFields.priceSideScript,
        subscriptText: consolidatedFields.priceSubScript,
      }}
      waitlistLink={consolidatedFields.waitlistLink}
      productUrl={getUrlFromStoryblokLink(blok.productUrl)}
      purchasableItem={purchasableItem}
      consolidatedProduct={consolidatedFields}
      contentSections={blok.variants[selectedOption].infoSections || []}
      deliveryTitle={blok.deliveryTitle}
      deliveryDescription={blok.deliveryDescription}
      variantDescription={blok.variantDescription}
      variantOptions={variantOptions}
      variantOptionsOnChange={handleVariantOptionChange}
      selectedVariantOption={selectedOption}
      showPayflexSection={blok.showPayflexSection}
      badge={storyblokItem.content?.badges?.[0]}
    />
  )
}

export default StoryblokProductInfoSection
